import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Form, Input, InputNumber, Select, Skeleton, Tag, Typography, message } from "antd";

import { type FreeMonthConfigData, type FreeMonthOfferId } from "@fitness-app/data-models/entities/FreeMonthConfig";
import {
  ProductChargeType,
  ProductDurationType,
  ProductStatus,
  type ProductChargeTypeEnum,
  type ProductStatusEnum,
} from "@fitness-app/data-models/entities/Product";
import { AppConfigType, type FreeMonthAppConfig } from "@fitness-app/data-models/entities/TrainerConfig";
import { type Json } from "@fitness-app/supabase";

import { supabase } from "~/store/initializeStore";

// Oferty „miesiąc gratis” z landinga /pakiety-1mcfree (TRE-2192) — app_config typu
// free_month. Produkty trzymamy tutaj, a nie w env, żeby staging i produkcja miały
// własne wartości zmieniane bez deployu. Pusty wybór wyłącza ofertę.

const queryKey = ["app_config", AppConfigType.FREE_MONTH];
const productsQueryKey = ["app_config", AppConfigType.FREE_MONTH, "products"];

interface OfferProduct {
  id: string;
  name: string;
  chargeType: ProductChargeTypeEnum | null;
  status: ProductStatusEnum | null;
  productDuration: { type?: string; count?: number; unit?: string; date?: string } | null;
}

const OFFER_FIELDS: { id: FreeMonthOfferId; label: string; tooltip: string }[] = [
  {
    id: "full",
    label: "Produkt: pełne prowadzenie",
    tooltip: "Aktywowany po wyborze prowadzenia z Mentorką. Brak wyboru wyłącza tę ścieżkę formularza.",
  },
  {
    id: "ai",
    label: "Produkt: Trenerka AI",
    tooltip: "Aktywowany po wyborze wariantu z Trenerką AI. Brak wyboru wyłącza tę ścieżkę formularza.",
  },
];

// Wydarzenie w Calendly dla /pakiety-1mcfree-call (TRE-2220): link do umawiania albo URI z API.
const CALENDLY_EVENT_TYPE_PATTERN = /^https:\/\/(api\.calendly\.com\/event_types\/|calendly\.com\/)[^\s]+$/;

const describeDuration = (duration: OfferProduct["productDuration"]) => {
  if (duration?.type === ProductDurationType.LastsFor && duration.count) {
    return duration.unit === "week" ? `${duration.count} tyg.` : `${duration.count} mies.`;
  }
  if (duration?.type === ProductDurationType.UntilDate && duration.date) {
    return `do ${duration.date}`;
  }
  if (duration?.type === ProductDurationType.Infinite) {
    return "bezterminowy";
  }
  return "brak długości";
};

/**
 * A paid product would be granted through `createFreeOrder` without payment, and an
 * inactive one is refused by the activation, so neither may be picked at all.
 */
const isEligible = (product: OfferProduct) =>
  product.chargeType === ProductChargeType.FREE && product.status === ProductStatus.ACTIVE;

/** Durations that do not end leave the client with a free product for good. */
const durationWarning = (product: OfferProduct) => {
  if (!product.productDuration || product.productDuration.type === ProductDurationType.Infinite) {
    return "dostęp bezterminowy, nie wygaśnie po miesiącu";
  }
  if (product.productDuration.type === ProductDurationType.UntilDate) {
    return "dostęp do stałej daty, nie zależy od dnia startu";
  }
  return null;
};

export const FreeMonthOffersCard = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FreeMonthConfigData>();
  const selectedOffers = Form.useWatch("offers", form);

  const configQuery = useQuery({
    queryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.FREE_MONTH)
        .maybeSingle<FreeMonthAppConfig>();

      if (error) throw error;

      return data;
    },
  });

  const productsQuery = useQuery({
    queryKey: productsQueryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product")
        .select("id, name, chargeType, status, productDuration")
        .order("name", { ascending: true });

      if (error) throw error;

      return (data ?? []) as OfferProduct[];
    },
  });

  const products = (productsQuery.data ?? []).filter(isEligible);
  const productById = new Map(products.map((product) => [product.id, product]));

  useEffect(() => {
    if (configQuery.data?.data) {
      form.setFieldsValue(configQuery.data.data);
    }
  }, [configQuery.data, form]);

  const updateConfigMutation = useMutation({
    mutationFn: async (values: FreeMonthConfigData) => {
      const config = configQuery.data;
      if (!config?.id) {
        throw new Error("Brak rekordu konfiguracji miesiąca gratis (app_config, typ free_month).");
      }

      const { data, error } = await supabase
        .from("app_config")
        .update({
          data: { ...config.data, ...values } as unknown as Json,
          updatedAt: new Date().toISOString(),
          version: (config.version ?? 0) + 1,
        })
        .eq("id", config.id)
        .eq("version", config.version ?? 0)
        .select()
        .maybeSingle<FreeMonthAppConfig>();

      if (error) throw error;
      if (!data) {
        await queryClient.invalidateQueries({ queryKey });
        // Zero rows means either a concurrent save (version moved) or RLS refusing the
        // update, and the two are indistinguishable from here.
        throw new Error(
          "Zapis nie objął żadnego wiersza: konfiguracja zmieniła się w innym miejscu albo Twoje konto nie ma prawa jej edytować (wymagane konto właściciela).",
        );
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      void message.success("Konfiguracja miesiąca gratis zapisana.");
    },
    onError: (error) => {
      void message.error(
        error instanceof Error
          ? error.message
          : "Nie udało się zapisać konfiguracji (zmiana wymaga konta właściciela).",
      );
    },
  });

  return (
    <Card size="small" title="Miesiąc gratis (landing /pakiety-1mcfree)" style={{ marginBottom: 16 }}>
      {configQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : configQuery.isError || !configQuery.data ? (
        <Alert
          type="warning"
          showIcon
          message="Brak konfiguracji miesiąca gratis"
          description="Nie znaleziono rekordu app_config typu free_month — formularz na landingu nie nada dostępu, dopóki go nie ma."
        />
      ) : (
        <Form form={form} layout="vertical" onFinish={(values) => updateConfigMutation.mutate(values)}>
          {OFFER_FIELDS.map((offer) => {
            const selectedId = selectedOffers?.[offer.id]?.productId ?? "";
            const selected = productById.get(selectedId);
            const warning = selected ? durationWarning(selected) : null;
            // A saved id that is no longer eligible (product archived or made paid) would
            // otherwise disappear from the select without the admin noticing.
            const missing = selectedId && !selected;

            return (
              <Form.Item
                key={offer.id}
                name={["offers", offer.id, "productId"]}
                label={offer.label}
                tooltip={offer.tooltip}
                extra={
                  selected ? (
                    <span>
                      Dostęp: {describeDuration(selected.productDuration)}
                      {warning ? (
                        <Tag color="warning" style={{ marginLeft: 8 }}>
                          {warning}
                        </Tag>
                      ) : null}
                    </span>
                  ) : missing ? (
                    <Tag color="error">Zapisany produkt nie jest już darmowy albo aktywny</Tag>
                  ) : (
                    "Ścieżka wyłączona"
                  )
                }
              >
                <Select
                  style={{ maxWidth: 420 }}
                  allowClear
                  showSearch
                  placeholder="Wybierz produkt"
                  loading={productsQuery.isLoading}
                  optionFilterProp="label"
                  options={products.map((product) => ({ value: product.id, label: product.name }))}
                />
              </Form.Item>
            );
          })}

          <Form.Item
            name="calendlyEventType"
            label="Wydarzenie w Calendly (/pakiety-1mcfree-call)"
            tooltip="Link do umawiania wydarzenia z Calendly. Puste pole oznacza „Dofinansowanie 1 miesiąc - Program Hollywood”. Inne wydarzenie musi należeć do zespołu sprzedaży (inaczej rezerwacje nie trafią do SalesCRM i zostanie domyślne) i mieć te same pytania w tej samej kolejności, inaczej rezerwacja się nie uda."
            rules={[
              {
                pattern: CALENDLY_EVENT_TYPE_PATTERN,
                message: "Wklej link z Calendly, np. https://calendly.com/sylwetkahollywood/nazwa-wydarzenia",
              },
            ]}
            normalize={(value: string) => value?.trim()}
          >
            <Input
              allowClear
              placeholder="https://calendly.com/sylwetkahollywood/indywidualna-konsultacja-program-hollywood-clone-8"
              style={{ maxWidth: 420 }}
            />
          </Form.Item>

          <Form.Item name="slackChannel" label="Kanał Slack z raportami">
            <Input placeholder="bezplatny-miesiac" style={{ maxWidth: 420 }} />
          </Form.Item>

          <Form.Item name="signInCheckDelayMinutes" label="Raport na Slacku po (minuty)">
            <InputNumber min={1} max={1440} />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={updateConfigMutation.isLoading}>
            Zapisz
          </Button>

          <Typography.Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 12 }}>
            Lista zawiera tylko aktywne produkty darmowe. Długość dostępu ustawia się na samym produkcie, nie tutaj.
            Zmiana działa od razu i dotyczy nowych aktywacji; klientki w trakcie zachowują swoją datę końca.
          </Typography.Paragraph>
        </Form>
      )}
    </Card>
  );
};
